import React, { useContext } from 'react';
import {
  Box,
  createStyles,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Auth } from 'aws-amplify';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { FormikHelpers } from 'formik';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import OnboardingClient from 'src/clients/OnboardingClient';
import {
  getOnboardingStepsData,
  OnboardingSteps,
} from 'src/legacy/components/Onboarding/onboardingSteps';
import {
  OnboardingFormProps,
  OnboardingFormValues,
} from 'src/legacy/components/Onboarding/OnboardingForms';
import { OnboardingPageLayout } from 'src/legacy/components/Onboarding/OnboardingPageLayout';
import OnboardingButton from 'src/legacy/components/Onboarding/OnboardingButton';
import { FlagsContext, PortalConfigContext, RouteContext } from 'src/context';
import { TrackEvent, TrackPartnerStackSignUp } from 'src/utils/AnalyticsUtils';
import * as Colors from 'src/theme/colors';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import UsersClient from 'src/clients/UsersClient';
import { useAnonymousId } from 'src/hooks/useAnonymousId';
import { OnboardingStepAvatar } from 'src/legacy/components/Onboarding/OnboardingStepAvatar';
import { OnboardingPageQueryParams } from 'src/legacy/components/Onboarding/onboardingTypes';
import { BackIcon } from 'src/legacy/components/Icons';
import Button from 'src/legacy/components/Button';
import { OnboardingFormCard } from 'src/legacy/components/Onboarding/OnboardingFormCard';
import { CognitoUserWithAttributes } from 'src/utils/CognitoUtils';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import { setAuthError } from 'src/store/auth/slice';
import { ensureApiError, ensureError } from 'src/utils/Errors';
import { CloneWorkspaceSubTitle } from 'src/legacy/components/Onboarding';
import { AxiosResponse } from 'axios';
import { alertSnackbar } from 'src/store/ui/actions';
import { PortalConfig } from 'src/entities/PortalConfig';
import { useGetCloneableStatusQuery } from 'src/services/api';

const trackableEventsInitialState: Record<string, boolean> = {
  Lead: false,
  StartTrial: false,
  CompleteRegistration: false,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    subTitleText: {
      color: Colors.GraySmall,
    },
    backButton: {
      position: 'fixed',

      '&:hover': {
        backgroundColor: 'transparent',
      },
      [theme.breakpoints.up('sm')]: {
        top: theme.spacing(6.25),
        left: theme.spacing(7.5),
      },
      [theme.breakpoints.down('xs')]: {
        top: theme.spacing(2.5),
        left: theme.spacing(2),
      },
    },
  }),
);

export const OnboardingSetupPage = () => {
  const classes = useStyles();
  // to prevent multi submission for a given analytics event,
  // we define a state that persists the events submission state.
  const [trackedEvents, setTrackedEvents] = React.useState(
    trackableEventsInitialState,
  );
  const dispatch = useAppDispatch();
  const portalConfig = React.useContext(PortalConfigContext);
  const { query } = React.useContext(RouteContext);
  const queryParams = query as OnboardingPageQueryParams;
  const {
    referred = '',
    source = '',
    prefill_email: prefillEmail = '',
    template_id: workspaceTemplateId = '',
    template_name: workspaceTemplateName = '',
    step: selectedStep,
    authSource,
  } = queryParams;

  const { data: cloneableStatusData } = useGetCloneableStatusQuery(
    workspaceTemplateId,
    {
      skip: !workspaceTemplateId,
    },
  );

  const showCloningText = Boolean(
    cloneableStatusData?.isCloneable && workspaceTemplateId,
  );
  const [submitting, setSubmitting] = React.useState(false);
  const onboardingStepsData = getOnboardingStepsData();
  const [onboardingStep, setOnboardingSteps] = React.useState(
    onboardingStepsData[OnboardingSteps.Start - 1],
  );

  // this is used to represent if we are still rendering the correct onboarding step
  // once the query params are read and step is rendered this is set to false
  const [isSelectingOnboardingStep, setSelectingOnboardingStep] =
    React.useState(authSource !== null);

  const savedAnonymousId = useAnonymousId();
  const [onboardingValues, setOnboardingValues] =
    React.useState<OnboardingFormValues>({
      referred,
      source,
      workspaceTemplateId: workspaceTemplateId || '',
      email: decodeURIComponent(prefillEmail),
      password: '',
      // get user name if it is present in the query param (e.g. after user log in with reusable email,
      // login page sets the users name in the query param)
      name: queryParams?.name || '',
      phone: '',
      foundBy: '',
      foundByCustom: '',
      companyName: '',
      portalUrl: '',
      industry: '',
      industryCustom: '',
      companySize: '',
      role: '',
      signoutRedirectURL: '',
      marketingSiteEnabled: false,
      anonymousId: '',
      clientType: '',
      clientCount: '',
    });

  const onboardingFormRef =
    React.useRef<FormikHelpers<OnboardingFormProps>>(null);

  /**
   * This method selects onboarding step based on the 'step' query param
   * which is used to navigate between steps via url.
   */
  const selectOnboardingStepByQueryParam = () => {
    const queryStep = selectedStep?.replace('-', '_') || 'create_account';
    switch (selectedStep) {
      case 'create-account':
        setOnboardingSteps(onboardingStepsData[OnboardingSteps.Start - 1]);
        break;
      case 'company-setup':
        setOnboardingSteps(onboardingStepsData[OnboardingSteps.SignUp - 1]);
        break;
      case 'create-portal':
        setOnboardingSteps(onboardingStepsData[OnboardingSteps.GoToPortal - 1]);
        break;

      default:
        setOnboardingSteps(onboardingStepsData[OnboardingSteps.Start - 1]);
    }
    const url = new URL(window.location.href);
    url.searchParams.set('stage', queryStep);
    window.history.pushState({}, '', url);
  };

  const progressCurrentStep = () => {
    if (onboardingStep.step < onboardingStepsData.length) {
      setOnboardingSteps(onboardingStepsData[onboardingStep.step]);
    }
  };

  /**
   * Progress the onboarding step. The step property of the onboarding step is
   * indexed started at 1. So if you look up the OnboardingSteps array by the `step`
   * property you get the next step
   * Adds stackId from the second step, first set stackId from the owner on the Create Portal
   */
  const handleOnboardingButtonClick = async (values: OnboardingFormValues) => {
    let isSuccess = true;
    setSubmitting(true);

    // We equal signoutRedirectURL to portalUrl,
    // signoutRedirectURL would be possible to change in the settings
    const valuesStaged = {
      ...values,
      anonymousId: analytics.user && analytics.user() ? savedAnonymousId : '',
      signoutRedirectURL: values?.portalUrl
        ? `${values?.portalUrl}.copilot.app`
        : '',
    };

    // identify onboarding user to have traits from onboarding flow
    const { password, email, ...onboardingIdentifyTraits } = valuesStaged;
    // set the email in global variable to enable enhanced
    // conversions for google ads in Google Tag Manager
    window.createAccountData_email = email;
    if (analytics && analytics.identify) {
      const trackableTraits = { ...onboardingIdentifyTraits, email };
      analytics.identify(email, trackableTraits, {
        integrations: {
          Amplitude: false, // disable amplitude identify during onboarding to avoid email being sent to Amplitude as id
        },
      });
    }

    try {
      setOnboardingValues({
        ...valuesStaged,
      });
      const url = new URL(window.location.href);

      if (onboardingStep.step === OnboardingSteps.SignUp) {
        url.searchParams.set('stage', 'company_details');
        window.history.pushState({}, '', url);
      }

      if (onboardingStep.step === OnboardingSteps.GoToPortal) {
        let portalOnboarding: AxiosResponse<PortalConfig> | null = null;
        try {
          portalOnboarding = await OnboardingClient.SavePortalOnboarding(
            portalConfig,
            valuesStaged,
          );
        } catch (err) {
          const e = ensureApiError(err);
          let { message: errorMessage } = e;
          dispatch(
            alertSnackbar({
              errorMessage,
            }),
          );
          isSuccess = false;
          return;
        }

        // after onboarding is complete, track the sign-up
        // event for third party services.
        TrackPartnerStackSignUp({
          email: valuesStaged.email,
          name: valuesStaged.name,
          customer_key: portalOnboarding.data.fields.portalHeader,
          provider_key: portalOnboarding.data.customerId,
        });

        // after onboarding is saved, we should be able to login to the portal
        // and redirect the user to the new page
        let user: CognitoUserWithAttributes | null = null;

        // on get started with another portal screen
        // when user selects an existing authenticated email
        // he will skip the password step, then sign-in with email
        // and password won't work, so we need to sign-in with email only
        // using Auth.currentAuthenticatedUser
        if (!password) {
          user = await Auth.currentAuthenticatedUser();

          // if we have a logged in user and the we were trying to onboard a user
          // with google as auth provider, we need to now refresh the token
          // This is required so that the token can contain the updated permissions
          // from the created portal / database user via the onboarding api.
          // To acheive this we redirect to the login page and set the query params
          // to confirm the login
          if (
            user &&
            valuesStaged.loginSource === CognitoHostedUIIdentityProvider.Google
          ) {
            // we use the customState to hold the redirect uris, so to redirect the user to the
            // created portal we set some query params
            // - action => this triggers the auto login in the login page
            // - next => after login is done, this redirects the user to the portal they were trying to login
            // - email => provided for error handling
            const next = `login?action=confirm-login&next=/portal/${portalOnboarding.data.id}&email=${user.attributes.email}`;
            await Auth.federatedSignIn({
              provider: CognitoHostedUIIdentityProvider.Google,
              customState: next,
            });

            // the login process will now be completed via the login page, so we can return from here.
            return isSuccess;
          }
        } else {
          user = await Auth.signIn(email, password);
        }

        // set the app user cookie so that it can be used to authenticate the user
        await UsersClient.setAppUserCookie(
          user as CognitoUser,
          portalOnboarding.data.fields.portalHeader,
          valuesStaged.portalUrl,
          email,
        );

        window.location.replace(
          `/portal/${portalOnboarding.data.fields.portalHeader}`,
        );
        return isSuccess;
      }

      if (onboardingStep.step === OnboardingSteps.Start) {
        url.searchParams.set('stage', 'create_portal');
        window.history.pushState({}, '', url);
        try {
          let user: CognitoUserWithAttributes | null = null;

          if (!valuesStaged.password) {
            // if the password is not set, then a google authed user might be logged in
            user = await Auth.currentAuthenticatedUser();
          } else {
            user = await Auth.signIn(valuesStaged.email, valuesStaged.password);
          }

          // if this is success email is used and password is correct
          // go to Create your portal account (pass email in next step)
          if (user) {
            // set the onboarding values name attribute to the user name
            const { given_name, family_name } = user.attributes;
            setOnboardingValues({
              ...valuesStaged,
              name: `${given_name} ${family_name}`,
            });
            progressCurrentStep();
          }
        } catch (e) {
          // The error returned from Auth.signIn is an error from Cognito which
          // does not have code and looks like:
          /* {
             message: "User does not exist.",
             __type: "UserNotFoundException"
          }
          */
          const { message } = ensureError(e);
          const userNotFound = message === 'User does not exist.';
          if (userNotFound) {
            // when user is not found, validate email domain and if successful go to Create your portal account
            try {
              dispatch(setAuthError(''));
              await UsersClient.validateEmailDomain(email);
              progressCurrentStep();
            } catch (emailErr) {
              onboardingFormRef.current?.setFieldError('email', message);
              return isSuccess;
            }
          }

          // user not logged in and error is NotAuthorizedException, so email IS being used but password is incorrect.
          // We need to show user an error that sign-in failed. Once they correctly auth we can actually
          // go to next step to create portal with existing user. This is important to prevent anyone
          // from just creating a portal with someone else's email without actually authenticating
          onboardingFormRef.current?.setFieldError(
            'password',
            'Sign in failed, password is incorrect, try again.',
          );
          return isSuccess;
        }
      }

      progressCurrentStep();
    } catch (ex) {
      console.error('Onboarding submit error', ex);
      isSuccess = false;
    }
    return isSuccess;
  };

  const handleClickSkip = () => {
    setOnboardingSteps(onboardingStepsData[onboardingStep.step]);
  };

  React.useEffect(() => {
    selectOnboardingStepByQueryParam();

    // this is used to auto progress the selected step to the next one
    // if the user is logged in
    // this is required only when signing up via google, which is checked via the
    // query param authSource.
    if (
      authSource === CognitoHostedUIIdentityProvider.Google &&
      selectedStep === 'company-setup'
    ) {
      setSelectingOnboardingStep(true);
      Auth.currentAuthenticatedUser()
        .then((user: CognitoUserWithAttributes) => {
          // if the user is available, then we can set the state and move to the next step
          setOnboardingValues({
            ...onboardingValues,
            name: `${user.attributes.given_name} ${user.attributes.family_name}`,
            email: user.attributes.email,
            loginSource: CognitoHostedUIIdentityProvider.Google,
            picture: user.attributes.picture,
          });
          setSelectingOnboardingStep(false);
        })
        .catch((e) => {
          console.error('error signing in the user', e);
          // todo show this error in ui
        });
    } else {
      setSelectingOnboardingStep(false);
    }
  }, [authSource, selectedStep]);

  /**
   * when there is a saved anonymousId, we need use that for analytics.
   * this is needed to be able to connect the event the user took on
   * the marketing site to the event on the portal.
   */
  React.useEffect(() => {
    if (analytics?.user && savedAnonymousId) {
      analytics.user().anonymousId(savedAnonymousId);
    }
    TrackEvent('Onboarding - Started');
  }, [savedAnonymousId]);

  const { OnboardingComponent } = onboardingStep;

  /**
   * Memoized function to render the onboarding button based on the
   * current progress step and loading state
   */
  const ButtonRenderer = React.useMemo(() => {
    const buttonRender = (enableSubmit: boolean) => (
      <>
        {onboardingStep.componentProps?.buttonHint && (
          <Box mb={2} pl={1} pr={1} textAlign="center">
            <BaseTypography className={classes.subTitleText}>
              {onboardingStep.componentProps?.buttonHint}
            </BaseTypography>
          </Box>
        )}
        <OnboardingButton
          type="submit"
          htmlId={`onboarding-step-${onboardingStep.step}-btn`}
          color="primary"
          variant="contained"
          isLoading={submitting}
          disabled={!enableSubmit}
          fullWidth
        >
          {onboardingStep.componentProps.buttonLabel}
        </OnboardingButton>
      </>
    );
    return buttonRender;
  }, [onboardingStep, submitting]);

  const handleClickBack = () => {
    if (onboardingStep.step === OnboardingSteps.SignUp) {
      setOnboardingSteps(onboardingStepsData[OnboardingSteps.Start - 1]);
    }

    if (onboardingStep.step === OnboardingSteps.GoToPortal) {
      setOnboardingSteps(onboardingStepsData[OnboardingSteps.SignUp - 1]);
    }
  };

  const handleAnalyticsEventTracked = (event: string) => {
    setTrackedEvents((prev) => ({
      ...prev,
      [event]: true,
    }));
  };

  const theme = useTheme();
  const isNonMobileWindow = useMediaQuery(theme.breakpoints.up('sm'));
  const { OnboardingDisabledReason } = useContext(FlagsContext);

  const googleSignInProgress = useAppSelector(
    (state) => state.auth.processingCallback,
  );

  return (
    <OnboardingPageLayout>
      <LoadingWrapper
        hideContentWhileLoading
        isLoading={googleSignInProgress || isSelectingOnboardingStep}
        color={isNonMobileWindow ? 'white' : undefined}
      >
        {onboardingStep.step !== OnboardingSteps.Start && (
          <Button
            data-testid="onboarding-back-btn"
            htmlId="onboarding-back-btn"
            className={classes.backButton}
            startIcon={<BackIcon style={{ fontSize: 10 }} />}
            onClick={handleClickBack}
          >
            <BaseTypography fontType="13Medium">Back</BaseTypography>
          </Button>
        )}
        <OnboardingFormCard>
          <OnboardingStepAvatar />
          <Box mt={1.5} mb={5} textAlign="center" px={{ xs: 2, sm: 0 }}>
            {onboardingStep.title && (
              <BaseTypography fontType="18Medium">
                {OnboardingDisabledReason
                  ? 'Sign-ups are temporarily paused'
                  : onboardingStep.title}
              </BaseTypography>
            )}
            {onboardingStep.subtitle && (
              <Box mt={0.25}>
                <BaseTypography className={classes.subTitleText}>
                  {OnboardingDisabledReason
                    ? OnboardingDisabledReason
                    : onboardingStep.subtitle}
                </BaseTypography>
                {showCloningText && (
                  <CloneWorkspaceSubTitle
                    templateName={workspaceTemplateName}
                  />
                )}
              </Box>
            )}
          </Box>
          <Box
            mt={
              onboardingStep.step !== OnboardingSteps.Start // considering first input top padding on step 2 and step 3
                ? -2.5
                : 0
            }
          >
            <OnboardingComponent
              {...onboardingStep.componentProps}
              initialValues={onboardingValues}
              handleSubmitDone={handleOnboardingButtonClick}
              handleClickSkip={handleClickSkip}
              buttonRenderer={ButtonRenderer}
              ref={onboardingFormRef}
              trackedEvents={trackedEvents}
              onAnalyticsEventTracked={handleAnalyticsEventTracked}
            />
          </Box>
        </OnboardingFormCard>
      </LoadingWrapper>
    </OnboardingPageLayout>
  );
};
